import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, X } from 'lucide-react';
import { NAMESPACES } from '@/i18n/namespaces';
import { useLocale } from '@/i18n/useLocale';
import reviewState from '@/i18n/review-state.generated.json';
import { cn } from '@/lib/utils';
import { COARSE_HIT_ALONE } from '@/lib/touch-target';
import { useReportTranslation } from './useReportTranslation';

/**
 * "This language has not been reviewed" — the honesty marker for a
 * machine-translated locale (#2109 rulings brief B23; DECISIONS 2026-09-22
 * "A non-English locale without a named TRF reviewer…").
 *
 * WHY THIS EXISTS. It is the sibling of `PrototypeBanner` and `MockupBanner`:
 * a machine translation's failure is not that it is unreadable, it is that
 * it reads convincingly. Until TRF's named native reviewer has moved every
 * namespace to `approved`, the shell says so, in that language, on every
 * page. The review state is read from `review-state.generated.json`, which
 * `scripts/i18n-review-state.mjs` derives from `i18n/state/<code>.json` —
 * a namespace missing from it is `draft`, never assumed approved.
 *
 * Dismissal is per language, per device (`localStorage['trf.mt-banner.<code>']`).
 * That is the DECISIONS trade-off: a family that dismissed it once is not
 * nagged on every page, and a second device shows it again.
 *
 * `useMachineBannerVisible()` exposes the same predicate to other surfaces
 * (#2431: the wizard's form-level machine line hides while this banner shows
 * and returns once it is dismissed). Dismissal is shared state, not local
 * component state, so every reader re-renders the moment it changes — in this
 * tab through the listeners below, in another tab through the storage event.
 */
export const DISMISS_KEY_PREFIX = 'trf.mt-banner.';

type ReviewState = 'draft' | 'machine' | 'reviewed' | 'approved';
type ReviewMap = Record<string, Record<string, ReviewState>>;

/** Dismissals made while storage is blocked: they last for this page load. */
const memoryDismissed = new Set<string>();
const listeners = new Set<() => void>();

function readDismissed(code: string): boolean {
  try {
    return window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${code}`) === '1';
  } catch {
    return memoryDismissed.has(code);
  }
}

function writeDismissed(code: string): void {
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${code}`, '1');
  } catch {
    // Private window or blocked site data: the dismissal lives for this page load.
    memoryDismissed.add(code);
  }
}

function dismiss(code: string): void {
  writeDismissed(code);
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith(DISMISS_KEY_PREFIX)) notify();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(notify);
    window.removeEventListener('storage', onStorage);
  };
}

/** True when any shipped namespace is below `approved` for this locale. */
function needsBanner(locale: string, state: ReviewMap = reviewState as ReviewMap): boolean {
  if (locale === 'en') return false;
  const byNamespace = state[locale] ?? {};
  return NAMESPACES.some((ns) => (byNamespace[ns] ?? 'draft') !== 'approved');
}

/**
 * True while the shell banner shows for the reader's language: the language
 * is below `approved` somewhere and the banner has not been dismissed here.
 */
export function useMachineBannerVisible(): boolean {
  const { locale } = useLocale();
  const dismissed = useSyncExternalStore(
    subscribe,
    () => readDismissed(locale),
    () => false,
  );
  return needsBanner(locale) && !dismissed;
}

export function MachineTranslationBanner({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { locale, bcp47 } = useLocale();
  const visible = useMachineBannerVisible();
  const report = useReportTranslation();

  if (!visible) return null;
  const full = t('language.machineTranslatedBanner');

  // Below md the banner is compact (#2431): caption type, tight padding, a
  // 24 px dismiss target, and no Languages icon of its own — in the shell the
  // switcher beside it carries the same icon. Measured at 390 px in Chromium,
  // the switcher row fell from 100 px to 50 px in pt and fr, 81 to 50 in es,
  // and 61 to 38 in zh; md and up is unchanged.
  //
  // The re-walk still measured 3 lines in fr and 2 in zh: beside the switcher
  // the text gets about 170 px at 390 px. So below md the visible line is the
  // short label ("Machine-translated") on one truncating line; the full
  // sentence stays in the DOM for screen readers and as the title, and is the
  // visible line from md up.
  //
  // #2645 (D-2420): the dismiss × stands alone at the banner's end, so on a
  // coarse pointer its ::after reaches 10 px past each edge (44 px) at its own
  // level. Above and below, the banner's own box has to hold that reach: in
  // the shell the header (a z-50 flex item) paints over anything that pokes
  // out under the row, so a coarse pointer gets `py-2` (the md padding)
  // instead of `py-1`, and the reach ends at the banner's edge.
  return (
    <div
      role="status"
      lang={bcp47}
      className={cn(
        // Quiet, not a highway banner (Victor, 2026-10-14): the shell's own
        // surface and hairline rule, muted ink, the Languages icon muted. The
        // sentence is still read by a screen reader and still says machine.
        'flex items-center gap-2 border-b border-stevens-rule bg-stevens-surface px-3 py-1 [@media(pointer:coarse)]:py-2 text-caption text-stevens-ink-2 md:gap-3 md:px-4 md:py-2 md:text-body-sm',
        className,
      )}
    >
      <Languages className="hidden h-3.5 w-3.5 shrink-0 text-stevens-ink-3 md:block" aria-hidden="true" />
      <p className="m-0 min-w-0 flex-1" title={full}>
        <span data-mt-banner="short" aria-hidden="true" className="block truncate md:hidden">
          {t('language.machineTranslatedShort')}
        </span>
        <span data-mt-banner="full" className="sr-only md:not-sr-only">
          {full}
        </span>
      </p>
      {/* #2120: "Report a translation", from md up. Below md the switcher
          beside the banner carries the same action as an icon (the same split
          as the Languages icon above), so the compact banner keeps its one
          truncating line. md:gap-3 (12 px) clears the dismiss button's 10 px
          coarse reach, and this button reaches only up and down (8 px on a
          28 px box inside the md py-2 banner), so neither steals the other's
          edge. */}
      {report.available && (
        <button
          type="button"
          {...report.triggerProps}
          className={cn(
            'hidden h-7 shrink-0 items-center rounded-md px-2 font-medium text-stevens-ink underline underline-offset-2 hover:bg-[color-mix(in_oklab,currentColor_12%,transparent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex',
            COARSE_HIT_ALONE,
            '[@media(pointer:coarse)]:after:-inset-y-2',
          )}
        >
          {t('language.report.action')}
        </button>
      )}
      {report.dialog}
      <button
        type="button"
        onClick={() => dismiss(locale)}
        aria-label={t('actions.dismiss')}
        className={cn(
          'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md hover:bg-[color-mix(in_oklab,currentColor_12%,transparent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-7 md:w-7',
          COARSE_HIT_ALONE,
          '[@media(pointer:coarse)]:after:-inset-2.5',
        )}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
